export type ClinicalFinalizeIssueLike = {
  ruleId: string;
  severity: 'warning' | 'blocking';
  message: string;
  guidelineName?: string;
  condition?: string;
  trigger?: string;
  dayNumber?: number;
  mealName?: string;
  itemType?: 'recipe' | 'ingredient';
  itemName?: string;
};

export type GroupedClinicalWarning = {
  key: string;
  ruleId: string;
  message: string;
  locations: string[];
  affectedItems?: Array<{
    itemName: string;
    locations: string[];
  }>;
  sortDay: number;
  sortMeal: number;
};

export type ClinicalWarningGroup = {
  key: string;
  title: string;
  warnings: GroupedClinicalWarning[];
  sortDay: number;
  sortMeal: number;
};

const RULE_ID_PATTERN = /^[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)+$/;
const MEAL_SORT = ['breakfast', 'morning snack', 'lunch', 'afternoon snack', 'snack', 'dinner'];
const DIETARY_PREFERENCE_RULE_ID = 'BLOCK-MANUAL-DIETARY-PREFERENCE-001';

const isRuleIdLike = (value: string): boolean => RULE_ID_PATTERN.test(value.trim());

const deriveTitleFromMessage = (message: string): string | null => {
  const exceeds = message.match(/exceeds the\s+(.+?):/i);
  if (exceeds?.[1]) return exceeds[1].trim().replace(/^the\s+/i, '');

  const limitFor = message.match(/(?:limit|minimum|cap)\s+for\s+(.+?)\s+is\b/i);
  if (limitFor?.[1]) return limitFor[1].trim();

  const beforeColon = message.split(':')[0]?.trim();
  if (
    beforeColon
    && beforeColon.length >= 8
    && beforeColon.length <= 52
    && !/^Day\s+\d+/i.test(beforeColon)
    && !isRuleIdLike(beforeColon)
  ) {
    return beforeColon;
  }

  return null;
};

export const getClinicalIssueCategory = (issue: ClinicalFinalizeIssueLike): string => {
  const candidates = [issue.condition, issue.guidelineName]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean);
  const humanReadable = candidates.find((value) => !isRuleIdLike(value));
  if (humanReadable) return humanReadable;
  return deriveTitleFromMessage(issue.message) || 'Clinical guidance';
};

const getMealSort = (mealName?: string): number => {
  const normalized = String(mealName || '').trim().toLowerCase();
  const index = MEAL_SORT.indexOf(normalized);
  return index === -1 ? MEAL_SORT.length : index;
};

const getLocation = (issue: ClinicalFinalizeIssueLike): string => {
  if (issue.dayNumber == null) return 'Plan-wide';
  const rawMealName = String(issue.mealName || '').trim();
  const mealName = rawMealName
    ? `${rawMealName.charAt(0).toLocaleUpperCase()}${rawMealName.slice(1)}`
    : '';
  return mealName ? `Day ${issue.dayNumber} · ${mealName}` : `Day ${issue.dayNumber}`;
};

const getDetailMessage = (issue: ClinicalFinalizeIssueLike): string => {
  let message = issue.message.trim();
  if (issue.dayNumber != null) {
    message = message.replace(
      new RegExp(`\\s*\\(Day\\s*${issue.dayNumber}(?:,\\s*[^)]+)?\\)\\s*$`, 'i'),
      '',
    );
  }
  return message.trim();
};

const normalizePreference = (issue: ClinicalFinalizeIssueLike): string => (
  String(issue.trigger || '')
    .trim()
    .toLocaleLowerCase()
    .replace(/[-_]+/g, ' ')
);

const collapseDominatedDietaryPreferences = (
  issues: ClinicalFinalizeIssueLike[],
): ClinicalFinalizeIssueLike[] => {
  const veganLocations = new Set(
    issues
      .filter((issue) => (
        issue.ruleId === DIETARY_PREFERENCE_RULE_ID
        && normalizePreference(issue) === 'vegan'
      ))
      .map((issue) => JSON.stringify([
        issue.severity,
        issue.dayNumber ?? 'plan',
        String(issue.mealName || '').trim().toLocaleLowerCase(),
        issue.itemType || '',
        String(issue.itemName || '').trim().toLocaleLowerCase(),
      ])),
  );

  return issues.filter((issue) => {
    if (
      issue.ruleId !== DIETARY_PREFERENCE_RULE_ID
      || normalizePreference(issue) !== 'vegetarian'
    ) return true;

    const locationKey = JSON.stringify([
      issue.severity,
      issue.dayNumber ?? 'plan',
      String(issue.mealName || '').trim().toLocaleLowerCase(),
      issue.itemType || '',
      String(issue.itemName || '').trim().toLocaleLowerCase(),
    ]);
    return !veganLocations.has(locationKey);
  });
};

const preferenceLabel = (preference: string): string => {
  if (!preference) return 'Dietary preference';
  return `${preference.charAt(0).toLocaleUpperCase()}${preference.slice(1)} preference`;
};

/**
 * Collapse identical clinical advice across meal slots while preserving every
 * affected location. The backend deliberately reports locations separately;
 * the UI presents the advice once and lists those locations together.
 */
export const groupClinicalFinalizeIssues = (
  issues: ClinicalFinalizeIssueLike[],
): ClinicalWarningGroup[] => {
  const groups = new Map<string, ClinicalWarningGroup>();

  collapseDominatedDietaryPreferences(issues).forEach((issue) => {
    const title = getClinicalIssueCategory(issue);
    const groupKey = title.toLocaleLowerCase();
    const detailMessage = getDetailMessage(issue);
    const isDietaryPreference = issue.ruleId === DIETARY_PREFERENCE_RULE_ID;
    const normalizedPreference = normalizePreference(issue);
    const warningKey = isDietaryPreference
      ? JSON.stringify([issue.ruleId, issue.severity, normalizedPreference])
      : JSON.stringify([
          issue.ruleId,
          issue.severity,
          issue.trigger || '',
          issue.itemType || '',
          issue.itemName || '',
          detailMessage,
        ]);
    const sortDay = issue.dayNumber ?? Number.MAX_SAFE_INTEGER;
    const sortMeal = getMealSort(issue.mealName);
    let group = groups.get(groupKey);
    if (!group) {
      group = {
        key: groupKey,
        title,
        warnings: [],
        sortDay,
        sortMeal,
      };
      groups.set(groupKey, group);
    } else if (sortDay < group.sortDay || (sortDay === group.sortDay && sortMeal < group.sortMeal)) {
      group.sortDay = sortDay;
      group.sortMeal = sortMeal;
    }

    const location = getLocation(issue);
    const existing = group.warnings.find((warning) => warning.key === warningKey);
    if (existing) {
      if (!existing.locations.includes(location)) existing.locations.push(location);
      if (isDietaryPreference && issue.itemName) {
        const itemName = issue.itemName.trim();
        const affectedItem = existing.affectedItems?.find((item) => item.itemName === itemName);
        if (affectedItem) {
          if (!affectedItem.locations.includes(location)) affectedItem.locations.push(location);
        } else {
          existing.affectedItems?.push({ itemName, locations: [location] });
        }
      }
      if (sortDay < existing.sortDay || (sortDay === existing.sortDay && sortMeal < existing.sortMeal)) {
        existing.sortDay = sortDay;
        existing.sortMeal = sortMeal;
      }
      return;
    }

    group.warnings.push({
      key: warningKey,
      ruleId: issue.ruleId,
      message: isDietaryPreference ? preferenceLabel(normalizedPreference) : detailMessage,
      locations: [location],
      ...(isDietaryPreference && issue.itemName
        ? { affectedItems: [{ itemName: issue.itemName.trim(), locations: [location] }] }
        : {}),
      sortDay,
      sortMeal,
    });
  });

  return [...groups.values()]
    .map((group) => ({
      ...group,
      warnings: group.warnings.sort((a, b) => (
        a.sortDay - b.sortDay
        || a.sortMeal - b.sortMeal
        || a.message.localeCompare(b.message)
      )),
    }))
    .sort((a, b) => (
      a.sortDay - b.sortDay
      || a.sortMeal - b.sortMeal
      || a.title.localeCompare(b.title)
    ));
};

export const countDistinctClinicalIssues = (issues: ClinicalFinalizeIssueLike[]): number => (
  groupClinicalFinalizeIssues(issues)
    .reduce((total, group) => total + group.warnings.length, 0)
);

export const countClinicalIssueGroups = (issues: ClinicalFinalizeIssueLike[]): number => (
  groupClinicalFinalizeIssues(issues).length
);

export const groupAffectedItemsByLocation = (
  warning: GroupedClinicalWarning,
): Array<{ location: string; itemNames: string[] }> => {
  const locations = new Map<string, string[]>();
  for (const affectedItem of warning.affectedItems || []) {
    for (const location of affectedItem.locations) {
      const itemNames = locations.get(location) || [];
      if (!itemNames.includes(affectedItem.itemName)) itemNames.push(affectedItem.itemName);
      locations.set(location, itemNames);
    }
  }
  return [...locations].map(([location, itemNames]) => ({ location, itemNames }));
};
